import { useState, useEffect } from 'react';
import { AlertCircle, CreditCard, ExternalLink, CheckCircle2, Clock, X, RefreshCw } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import StripeConnectOnboardingModal from './StripeConnectOnboardingModal';

const StripeConnectBanner = ({ onDismiss }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);

  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = async () => {
    try {
      const response = await api.get('/organizations/stripe-connect/status');
      setStatus(response.data);
    } catch (error) {
      console.error('Error checking Stripe Connect status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = () => {
    setShowOnboardingModal(true);
  };

  const handleOnboardingComplete = () => {
    setShowOnboardingModal(false);
    // Refresh status
    checkStatus();
    toast.success('Stripe account connected successfully!');
  };

  const handleRefresh = () => {
    setShowOnboardingModal(true);
  };

  if (loading) {
    return null;
  }

  // Don't show if already fully connected
  if (status?.onboardingComplete) {
    return null;
  }

  // Show different banners based on status
  if (!status?.connected) {
    // Not connected at all
    return (
      <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-4 sm:p-6 relative">
        {onDismiss && (
          <button
            onClick={onDismiss}
            className="absolute top-2 right-2 sm:top-4 sm:right-4 text-gray-400 hover:text-gray-600"
          >
            <X className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        )}
        
        <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4">
          <div className="bg-blue-600 p-2 sm:p-3 rounded-xl flex-shrink-0">
            <CreditCard className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">
              💰 Set Up Payment Processing
            </h3>
            <p className="text-sm sm:text-base text-gray-700 mb-3">
              Connect your payment account to start receiving payments from customers. 
              Setup takes just 2-3 minutes. Payments go directly to your bank account.
            </p>
            <p className="text-xs sm:text-sm text-gray-600 mb-4">
              ⏱️ <em>Note: Verification may take a few minutes after submitting your form. Hang tight!</em>
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap gap-3">
              <button
                onClick={handleConnect}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 sm:px-6 py-2.5 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors text-sm sm:text-base"
              >
                <CreditCard className="w-4 h-4" />
                Connect Payment Account
              </button>
              <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600 justify-center sm:justify-start">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                Free • Secure • Takes 2-3 min
              </div>
            </div>
          </div>
        </div>
        
        {/* Onboarding Modal */}
        <StripeConnectOnboardingModal
          isOpen={showOnboardingModal}
          onClose={() => setShowOnboardingModal(false)}
          onComplete={handleOnboardingComplete}
        />
      </div>
    );
  }

  // Connected but onboarding incomplete
  if (status?.connected && !status?.onboardingComplete) {
    return (
      <div className="mb-6">
        {/* Progress Indicator - Mobile Responsive */}
        <div className="flex items-center justify-center gap-1 sm:gap-2 mb-6">
          <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs sm:text-sm font-semibold">
            ✓
          </div>
          <div className="w-8 sm:w-12 h-1 bg-green-600" />
          <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-green-600 text-white flex items-center justify-center text-xs sm:text-sm font-semibold">
            2
          </div>
          <div className="w-8 sm:w-12 h-1 bg-gray-200" />
          <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-gray-200 text-gray-500 flex items-center justify-center text-xs sm:text-sm font-semibold">
            3
          </div>
        </div>
      
        {/* Card */}
        <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4 sm:p-6 relative">
          {onDismiss && (
            <button
              onClick={onDismiss}
              className="absolute top-2 right-2 sm:top-4 sm:right-4 text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          )}
          
          <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4">
          <div className="bg-green-600 p-2 sm:p-3 rounded-xl flex-shrink-0">
            <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">
              Almost There! 🎉
            </h3>
            <p className="text-sm sm:text-base text-gray-700 mb-2">
              Great progress! Your documents for your business were verified successfully. ✓
            </p>
            <p className="text-sm sm:text-base text-gray-700 mb-4">
              We just need to verify a few more details about you (the owner) to finish setting up payments. Just a few clicks away!
            </p>
            {status?.requirements?.currentlyDue?.length > 0 && (
              <div className="bg-green-50 rounded-lg p-3 sm:p-4 mb-4 border border-green-200">
                <p className="text-xs sm:text-sm font-semibold text-green-900 mb-2">Quick verifications needed:</p>
                <ul className="text-xs sm:text-sm text-gray-700 space-y-1 sm:space-y-1.5">
                  {status.requirements.currentlyDue.slice(0, 5).map((req, idx) => (
                    <li key={idx} className="flex items-start gap-1.5 sm:gap-2">
                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full flex-shrink-0 mt-1.5" />
                      <span className="capitalize break-all flex-1">{req.replace(/_/g, ' ').replace('individual', 'your')}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <button
                onClick={handleRefresh}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2.5 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors text-sm sm:text-base"
              >
                <CheckCircle2 className="w-4 h-4" />
                Verify Owner Info
              </button>
              <button
                onClick={() => window.location.reload()}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2.5 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors text-sm sm:text-base"
              >
                <RefreshCw className="w-4 h-4" />
                <span className="hidden sm:inline">Refresh Status</span>
                <span className="sm:hidden">Refresh</span>
              </button>
            </div>
          </div>
        </div>
        </div>
        
        {/* Onboarding Modal */}
        <StripeConnectOnboardingModal
          isOpen={showOnboardingModal}
          onClose={() => setShowOnboardingModal(false)}
          onComplete={handleOnboardingComplete}
        />
      </div>
    );
  }

  return null;
};

export default StripeConnectBanner;
